import MiiaFwConfig from '@/configs/config'
import { type ErrorCode, type SupportInfo } from './entities/valueObjects/types'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { ERROR_MESSAGES, UNKNOWN_MESSAGE } from '@/constants/error/errorMessage'

/**
 * @description 集約例外に関する機能をまとめたクラス
 */
export class GlobalError extends Error {
  private static isDisplayedDialog: boolean = false
  private errorCodes: ErrorCode[]
  private readonly errorArgs: string[]
  private readonly supportInfo: SupportInfo
  private readonly replacementNotes: string[]

  /**
   * コンストラクタ
   * @param errorCodes エラーコード、先頭にあるエラーコードがエラーメッセージとして出力される
   * @param options.errorArgs エラーメッセージの文字引数
   * @param options.supportInfo カスタマーサポート情報
   * @param options.replacementNotes 補足情報の置き換え文字の配列
   * @param options.message 元のErrorクラスのメッセージ
   */
  constructor(
    errorCodes: ErrorCode | ErrorCode[],
    options?: {
      errorArgs?: string[]
      supportInfo?: SupportInfo
      replacementNotes?: string[]
      message?: string
    }
  ) {
    super(options?.message)
    this.errorCodes = Array.isArray(errorCodes) ? errorCodes : [errorCodes]
    this.errorArgs = options?.errorArgs !== undefined ? options.errorArgs : []
    this.supportInfo = options?.supportInfo !== undefined ? options.supportInfo : defaultSupport
    this.replacementNotes = options?.replacementNotes === undefined ? [] : options.replacementNotes
  }

  /**
   * エラーメッセージを組み立てる
   * @param globalErrorCodes エラーコードの配列
   * @return エラーメッセージ
   */
  private makeErrorMessage(globalErrorCodes: ErrorCode[]): string {
    // エラーコードが存在しない場合は共通メッセージの不明なエラーとして出す。メッセージは仮置き
    const errorInfo = ERROR_MESSAGES[globalErrorCodes[0]] ?? UNKNOWN_MESSAGE

    let message = `${errorInfo.message}<br><br>`
    this.errorArgs.forEach((arg, index) => (message = message.replace(`{${index}}`, arg)))

    // CS問い合わせが必要な場合は、連絡先を追加する
    if (errorInfo.needsSupport !== undefined && errorInfo.needsSupport) {
      message += 'カスタマーサポート連絡先<br>'
      message += `電話番号: ${this.supportInfo.phoneNumber}<br>`
      message += `メールアドレス: ${this.supportInfo.email}<br><br>`
    }

    // エラーコードを全て表示(空文字だったり、undefinedはfilterで除外)
    message += `エラーコード: ${globalErrorCodes.filter(code => code).join(', ')}`

    // CS問い合わせが必要な場合の補足情報、空や未定義の場合は表示しない
    if (
      errorInfo.needsSupport !== undefined &&
      errorInfo.needsSupport &&
      errorInfo.note !== undefined &&
      errorInfo.note !== ''
    ) {
      message += '<br><br>補足情報<br>'
      let note = errorInfo.note
      this.replacementNotes.forEach((replacementNote, index) => {
        const replacementString = `#replacement${index + 1}#`
        note = note.replace(replacementString, replacementNote)
      })
      message += `${note}<br>`
    }

    return message
  }

  /**
   * エラーメッセージに記載されているタイトルを取得する。存在しない場合や空文字の場合はシステム名が出力される
   * @param errorCode エラーコード
   * @returns エラーダイアログのタイトル
   */
  private getDialogTitle(errorCode: ErrorCode): string {
    const errorInfo = ERROR_MESSAGES[errorCode]
    return errorInfo.title === undefined || errorInfo.title === ''
      ? MiiaFwConfig.config.sysName
      : errorInfo.title
  }

  /**
   * エラーコードを末尾に追加する
   * @param errorCode エラーコード
   * @return 自分自身の参照
   */
  addErrorCodeLast(errorCode: ErrorCode): GlobalError {
    this.errorCodes = this.errorCodes.concat(errorCode)
    return this
  }

  /**
   * エラーコードを先頭に追加する
   * @param errorCode エラーコード
   * @return 自分自身の参照
   */
  addErrorCodeFirst(errorCode: ErrorCode): GlobalError {
    this.errorCodes = [errorCode, ...this.errorCodes]
    return this
  }

  /**
   * エラーメッセージを表示する
   * @return 戻り値なし
   */
  showErrorMessage(): void {
    if (GlobalError.isDisplayedDialog) {
      return
    }
    GlobalError.isDisplayedDialog = true
    const commonModalStore = useCommonModalStore()
    commonModalStore
      .openAlertModal({
        title: this.getDialogTitle(this.errorCodes[0]),
        body: this.makeErrorMessage(this.errorCodes),
        okTitle: '確認'
      })
      .finally(() => (GlobalError.isDisplayedDialog = false))
  }

  /**
   * 指定したエラーコードが含まれているか判定する
   * @param code 判定対象のエラーコード
   * @returns 含まれている場合はtrueを返す。
   */
  includes(code: ErrorCode): boolean {
    return this.errorCodes.includes(code)
  }

  /**
   * エラーオブジェクトにエラーコードを追加する。
   * 追加対象のエラーオブジェクトが不明なオブジェクトの場合は新しいインスタンスを作成する。
   * @param sourceError 追加対象のエラーオブジェクト
   * @param errorCode 追加するエラーコード
   * @returns エラーオブジェクト
   */
  public static chainError(sourceError: unknown, errorCode: ErrorCode): GlobalError {
    if (sourceError instanceof GlobalError) {
      sourceError.addErrorCodeFirst(errorCode)
      return sourceError
    }
    return new GlobalError(errorCode, { message: String(sourceError) })
  }
}

// デフォルトの問い合わせ情報、DBにアクセスできなかったときなどに使用する
const defaultSupport: SupportInfo = {
  phoneNumber: '0120-000-000',
  email: 'xxx@test.com'
}
